/**
 * Polaris Click Tracking Utilities
 *
 * Builds tracking strings for Polaris HP DynamicTabbedLayout
 * per the tracking format: position|description|linkType
 *
 * - position -> eVar164 (PAGE LOCATION)
 * - description -> eVar165 (LINK DESCRIPTION)
 *
 * Tab clicks:  HP-PolarisCluster|HP-Tab-Tab{N}-{TabName}|internal
 * Card clicks: HP-Tab-Tab{N}-{TabName}|HP-Card-Tab{N}-{TabName}-Card{M}-{Shape}-{Category}-{Desc}|internal
 */

const CONTENT_TYPE_CATEGORY_MAP: Record<string, string> = {
  destinations: 'Destination',
  properties: 'Brand',
  rnb: 'RnB',
  offers: 'Offer',
};

export function buildTabTrackingPrefix(tabIndex: number, tabName: string): string {
  return `HP-Tab-Tab${tabIndex}-${tabName}`;
}

function capitalizeShape(size: string): string {
  if (!size) return '';
  return size.charAt(0).toUpperCase() + size.slice(1);
}

export function mapContentTypeToCategory(contentType: string | undefined | null): string {
  if (!contentType) return '';
  return CONTENT_TYPE_CATEGORY_MAP[contentType.toLowerCase()] || contentType;
}

export function mapDcaTypeToCategory(metadata: Record<string, unknown> | undefined): string {
  if (!metadata) return 'Merch';
  const type = (metadata['type'] as string)?.toLowerCase();
  if (type === 'cobrand') return 'MerchCobrand';
  return 'Merch';
}

export function sanitizeTrackingValue(value: string | undefined | null): string {
  if (!value) return '';
  return value.replace(/[\s,|]+/g, '');
}

export function buildCardTrackingDescription(ctx: {
  tabIndex: number;
  tabName: string;
  cardNumber: number;
  shape: string;
  category: string;
  description: string;
  cta?: string;
}): string {
  const parts = [
    'HP-Card',
    `Tab${ctx.tabIndex}-${ctx.tabName}`,
    `Card${ctx.cardNumber}`,
    capitalizeShape(ctx.shape),
    ctx.category,
    ctx.description,
  ].filter(Boolean);
  if (ctx.cta) parts.push(ctx.cta);
  return parts.join('-');
}

export function resolveCardCategory(
  contentObj: Record<string, unknown>,
  slotType: 'cf' | 'dca' | string | undefined
): string {
  if (slotType === 'dca') {
    const metadata = (contentObj['metaData'] ?? contentObj['metadata']) as Record<string, unknown> | undefined;
    return mapDcaTypeToCategory(metadata);
  }
  return mapContentTypeToCategory(contentObj['contentType'] as string);
}

export function resolveCardDescription(
  contentObj: Record<string, unknown>,
  slotType: 'cf' | 'dca' | string | undefined
): string {
  if (slotType === 'dca') {
    const metadata = (contentObj['metaData'] ?? contentObj['metadata']) as Record<string, unknown> | undefined;
    return (
      sanitizeTrackingValue((metadata?.['evar48'] as string) || (contentObj['atCCeVar48'] as string)) ||
      sanitizeTrackingValue(contentObj['title'] as string) ||
      sanitizeTrackingValue(contentObj['headerLong'] as string) ||
      ''
    );
  }
  return (
    sanitizeTrackingValue(contentObj['trackingDescription'] as string) ||
    sanitizeTrackingValue(contentObj['title'] as string) ||
    sanitizeTrackingValue(contentObj['header'] as string) ||
    ''
  );
}
